import { Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import { getContact, getSocials } from '@/data/site';
import { useLiveTick } from '@/lib/liveContent';
import { Reveal } from '../Reveal';

// Premium contact-information panel using ONLY verified global contact
// details. No invented opening hours. Static location panel with a Get
// Directions link — no heavy third-party map embed.
export const ContactDetails = () => {
  useLiveTick();
  const CONTACT = getContact();
  const SOCIALS = getSocials();
  const linkedIn = SOCIALS.find((s) => s.icon === 'linkedin');
  const DIRECTIONS_URL = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    CONTACT.address
  )}`;

  return (
    <section aria-labelledby="contact-details-heading" data-testid="contact-details" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="flex items-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest text-gold-700">06</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">Details</span>
          </p>
          <h2 id="contact-details-heading" data-testid="contact-details-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            Contact details
          </h2>
        </div>

        <Reveal>
          <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            <a
              href={CONTACT.phoneHref}
              data-testid="contact-phone-link"
              className="group flex flex-col gap-3 bg-white p-7 transition-colors hover:bg-mist"
            >
              <Phone className="h-5 w-5 text-gold-700" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Phone</span>
              <span className="text-base font-bold text-navy-900 group-hover:underline">{CONTACT.phone}</span>
            </a>
            <a
              href={CONTACT.emailHref}
              data-testid="contact-email-link"
              className="group flex flex-col gap-3 bg-white p-7 transition-colors hover:bg-mist"
            >
              <Mail className="h-5 w-5 text-gold-700" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Email</span>
              <span className="text-base font-bold text-navy-900 group-hover:underline">{CONTACT.email}</span>
            </a>
            <div className="flex flex-col gap-3 bg-white p-7" data-testid="contact-address">
              <MapPin className="h-5 w-5 text-gold-700" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Office</span>
              <span className="text-base font-bold leading-snug text-navy-900">{CONTACT.address}</span>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-directions-link"
                className="mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-gold-700 underline-offset-2 transition-colors hover:text-gold-700 hover:underline"
              >
                Get Directions
              </a>
            </div>
            {linkedIn && (
              <a
                href={linkedIn.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin-link"
                className="group flex flex-col gap-3 bg-white p-7 transition-colors hover:bg-mist"
              >
                <Linkedin className="h-5 w-5 text-gold-700" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">LinkedIn</span>
                <span className="text-base font-bold text-navy-900 group-hover:underline">
                  Follow Amden Group
                </span>
              </a>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
